import { lazy, Suspense, useEffect, useState } from 'react'

import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'

import { LayoutDashboardIcon, PlusIcon } from 'lucide-react'

import {
	Section,
	SectionDescription,
	SectionTitle,
} from '@/features/app-shell/section'
import { AddMenu } from '@/features/custom-dashboards/components/add-menu'
import { CreateDashboardDialog } from '@/features/custom-dashboards/components/create-dashboard-dialog'
import { DashboardStaleBanner } from '@/features/custom-dashboards/components/dashboard-stale-banner'
import { DashboardSwitcher } from '@/features/custom-dashboards/components/dashboard-switcher'
import { DashboardToolbar } from '@/features/custom-dashboards/components/dashboard-toolbar'
import { LoadFailureNotice } from '@/features/custom-dashboards/components/load-failure-notice'
import { UnreadableWidgetsNotice } from '@/features/custom-dashboards/components/unreadable-widgets-notice'
import { WidgetMenu } from '@/features/custom-dashboards/components/widget-menu'
import { useDashboardConfig } from '@/features/custom-dashboards/persistence-queries'
import {
	useDraftActions,
	useDraftKey,
} from '@/features/custom-dashboards/stores/draft-store'
import { useCanManageDashboards } from '@/features/custom-dashboards/use-can-manage-dashboards'
import { useDraftLifecycle } from '@/features/custom-dashboards/use-draft-lifecycle'
import { useEffectiveDashboard } from '@/features/custom-dashboards/use-effective-dashboard'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

// The grid pulls react-grid-layout + its CSS (heavy, only needed once a dashboard has
// widgets to lay out) — code-split it off the index route (P-21). Named → default.
const DashboardView = lazy(() =>
	import('@/features/custom-dashboards/components/dashboard-view').then(
		(m) => ({
			default: m.DashboardView,
		}),
	),
)

/**
 * Dashboard index surface. Renders the dashboard named by the `dashboard` URL
 * search param — nothing else. A bare `/custom-dashboards` (no param) redirects
 * to the Library list instead of landing on a resolved default: the owner's
 * call is that the main "Custom Dashboards" entry is a list, and the switcher's
 * pin/default entries are quick links straight to an id, not something this
 * route re-resolves. `?dashboard=<id>` is how the sidebar entries, pins, and the
 * switcher's own default/pin picks all land here (`sidebar-dashboard-entries.tsx`).
 * Zero dashboards, and a `?dashboard=` id that no longer exists, are each a
 * first-class state, never a blank screen (P-17). Replaces the Phase-1
 * `SampleDashboard` scaffold.
 */
export const Route = createFileRoute('/_dashboard/custom-dashboards/')({
	beforeLoad: ({ search }) => {
		// The ONE redirect this route makes: no `?dashboard=` means there is
		// nothing to render, so send the bare URL to the Library rather than
		// resolving a "best" dashboard to land on (that ladder — pin, then
		// Tenant default, then first — is retired for this route; see
		// `resolve-active-dashboard.ts`). No `replace`, matching this repo's
		// other index→canonical-child redirects (`/activity`, `/graylog`,
		// `/mdr`, `/air`) — none of those trap the back button either.
		if (!search.dashboard) {
			throw redirect({ to: '/custom-dashboards/library' })
		}
	},
	component: CustomDashboardsIndex,
})

function CustomDashboardsIndex() {
	const navigate = useNavigate()
	const search = Route.useSearch()
	const {
		data: config,
		isPending,
		isError,
		error,
		refetch,
	} = useDashboardConfig()
	// Read is every role; every write below is Analyst+ (see the layout route's
	// permission table). Not rendered rather than disabled, throughout.
	const canManage = useCanManageDashboards()

	const dashboards = config ? Object.values(config.dashboards) : []
	// Direct lookup, not a resolve ladder: `beforeLoad` above already sent a
	// param-less URL to the Library, so every render here has a `?dashboard=` to
	// look up. The pin/default rungs `resolveActiveDashboard` used to fall back
	// through are still there — as quick links the switcher's dropdown writes
	// straight into this same param — they are just never re-derived here.
	const activeDashboard = dashboards.find((d) => d.id === search.dashboard)

	// What the user is looking at: the record with any persisted draft substituted
	// wholesale (ADR 0024). Every placed widget is queried with its time/filters — a
	// widget's own saved time range is only the builder/preview default. The empty-id
	// fallback keeps the hook calls unconditional while the config loads or the
	// tenant has no dashboards; nothing renders from them until the gate below.
	const { dashboard: effectiveDashboard } = useEffectiveDashboard(
		activeDashboard?.id ?? '',
	)
	const draftKey = useDraftKey(activeDashboard?.id ?? '')
	const { setPlacements } = useDraftActions()

	// The distinct entities the active dashboard's placed widgets query — scopes which
	// fields the toolbar's filter controls offer. Effective placements on purpose: a
	// draft-added widget's fields must be filterable before the draft is saved.
	const entitiesInUse = config
		? [
				...new Set(
					(effectiveDashboard?.placements ?? [])
						.map((p) => config.widgets[p.widgetId]?.querySpec.entity)
						.filter((entity): entity is string => !!entity),
				),
			]
		: []

	// Writes the resolved/selected dashboard id back into the URL — makes the
	// switcher, "create", and delete-active all converge on one code path.
	const goTo = (dashboardId: string | undefined) =>
		navigate({
			to: '.',
			search: (prev) => ({ ...prev, dashboard: dashboardId }),
		})

	// The ways a draft dies that the user has to be told about (spec §5): a quota
	// eviction, a draft-schema bump, and a dashboard someone else deleted out from
	// under an unsaved arrangement. It needs the records because only they carry the
	// name a toast has to say — `dashboards` is deliberately left `undefined` on a
	// failed read, which is not evidence that anything was deleted.
	useDraftLifecycle({
		dashboards: config ? dashboards : undefined,
		isConfigResolved: !isPending,
		urlDashboardId: search.dashboard,
		activeDashboardId: activeDashboard?.id,
		onRouteAway: goTo,
	})

	// A `?dashboard=` id that does not resolve — a stale deep link, a since-deleted
	// dashboard reached without an unsaved draft (the case `useDraftLifecycle` above
	// does NOT cover, since it only reroutes ids with a drafted arrangement) — has
	// nothing to render. Without this, the toolbar/canvas block below simply never
	// gates open, and the user is left looking at the header row over blank space
	// (P-17: no blank screens). Library, not a resolved fallback dashboard, per the
	// "no resolve needed" call above; guarded on `dashboards.length` so a genuinely
	// empty tenant keeps rendering `EmptyLibrary` instead of racing it to a redirect.
	useEffect(() => {
		if (isPending || !search.dashboard || activeDashboard) return
		if (dashboards.length === 0) return
		void navigate({ to: '/custom-dashboards/library', replace: true })
	}, [
		isPending,
		search.dashboard,
		activeDashboard,
		dashboards.length,
		navigate,
	])

	return (
		<Section>
			<div className="flex items-center justify-between gap-4">
				<div>
					<SectionTitle>Custom Dashboards</SectionTitle>
					<SectionDescription>
						Build standalone widgets from live queries and arrange them into
						dashboards.
					</SectionDescription>
				</div>

				<div className="flex items-center gap-2">
					<DashboardSwitcher
						dashboards={dashboards}
						activeDashboard={activeDashboard}
						defaultDashboardId={config?.defaultDashboardId}
						onSelect={goTo}
						onSaved={goTo}
						onDeletedActive={goTo}
					/>

					{/*
					 * One "+ Add" dropdown where Library / "Add existing" / "New widget"
					 * stood (ticket 31). The menu is Analyst+ (every entry but Library is
					 * a write path). Read Only's plain Library button was removed by
					 * ticket 34: the sidebar's Library entry is their route there now,
					 * and the mock keeps the Library out of this header. The menu's own
					 * Library item stays — it deep-links to the Widgets tab, which the
					 * sidebar entry (resting on Dashboards) does not.
					 */}
					{canManage && (
						// Keyed so a dashboard switch/delete remounts the menu: its
						// "add existing" dialog is per-dashboard state, and a stale
						// open flag would otherwise reopen the dialog against the
						// NEXT dashboard the moment one resolves.
						<AddMenu
							key={activeDashboard?.id ?? 'no-dashboard'}
							activeDashboardId={activeDashboard?.id}
						/>
					)}
				</div>
			</div>

			{isError && (
				<LoadFailureNotice
					error={error}
					subject="dashboards"
					onRetry={() => void refetch()}
				/>
			)}

			{/*
			 * Above everything the config feeds, including the empty state: a tenant whose
			 * ONLY widgets are unreadable would otherwise read as "no dashboards yet"
			 * (ticket 45).
			 */}
			{config && <UnreadableWidgetsNotice widgets={config.unreadableWidgets} />}

			{!isError && !isPending && dashboards.length === 0 && (
				<EmptyLibrary canManage={canManage} onCreated={goTo} />
			)}

			{activeDashboard && effectiveDashboard && config && (
				/*
				 * Load-bearing wrapper: it is the toolbar's sticky containing block. `Section`'s
				 * own box is `h-full`, i.e. one viewport tall while the canvas overflows past it,
				 * so sticking inside THAT box unsticks the row one screen down — it reads as
				 * "sticky works" on a short dashboard and fails on a long one. This div is
				 * content-tall, so the toolbar stays pinned for the whole scroll. Don't flatten
				 * it back to a fragment.
				 */
				<div className="flex flex-col gap-4">
					{/*
					 * Above the toolbar and NOT sticky: the toolbar is the row that has to
					 * stay reachable down a scrolled canvas, and a second pinned row would
					 * eat the viewport for something the user can answer once.
					 */}
					<DashboardStaleBanner dashboardId={activeDashboard.id} />

					<DashboardToolbar
						dashboardId={activeDashboard.id}
						entitiesInUse={entitiesInUse}
					/>

					{/*
					 * P-19: the canvas is its own blast radius. Each cell is already
					 * fault-isolated by `WidgetCard`, so what lands here is the grid itself
					 * failing — a render bug or a broken layout — and without a boundary that
					 * takes the whole route down, toolbar included. A dead chunk is NOT this
					 * boundary's job: `main.tsx` reloads the page on `vite:preloadError`,
					 * because React caches a rejected `lazy` and "Try Again" could never
					 * clear it.
					 */}
					<ErrorBoundary
						feature="custom-dashboards"
						section="dashboard-canvas"
						entityId={activeDashboard.id}
					>
						<Suspense fallback={<GridSkeleton />}>
							<DashboardView
								placements={effectiveDashboard.placements}
								widgetsById={config.widgets}
								// So a cell left behind by a DROPPED widget says so, rather
								// than borrowing the deleted-widget wording (ticket 45).
								unreadableWidgetIds={
									new Set(config.unreadableWidgets.map((w) => w.id))
								}
								global={{
									globalTimeRange: effectiveDashboard.globalTimeRange,
									globalFilters: effectiveDashboard.globalFilters,
								}}
								// Layout persists on drag-stop, so a Read Only drag is a
								// guaranteed 403 and a snap-back — the grid is inert for them.
								canManage={canManage}
								// A drag/resize is an unsaved edit: the full next arrangement
								// goes into the persisted draft, seeded from the server record
								// (ADR 0024). Nothing reaches the server until Save.
								onPlacementsChange={(next) => {
									if (draftKey)
										setPlacements(
											draftKey,
											activeDashboard,
											activeDashboard.updatedAt,
											next,
										)
								}}
								// Every item in this menu is an entry point to a
								// ManageDashboards write, so a Read Only user gets no menu
								// rather than an empty one.
								renderWidgetMenu={
									canManage
										? (widget) => (
												<WidgetMenu
													widget={widget}
													dashboardId={activeDashboard.id}
												/>
											)
										: undefined
								}
							/>
						</Suspense>
					</ErrorBoundary>
				</div>
			)}
		</Section>
	)
}

/** Shape-matched fallback for the lazy grid chunk — two half-width card placeholders. */
function GridSkeleton() {
	return (
		<div className="grid min-h-40 grid-cols-1 gap-4 lg:grid-cols-2">
			<Skeleton className="h-80 w-full" />
			<Skeleton className="h-80 w-full" />
		</div>
	)
}

/**
 * First-landing empty state: no dashboards exist yet for this tenant. A Read Only user
 * gets the state without the invitation — creating one is a write they cannot make, and
 * an offer they can't take is worse than no offer.
 */
function EmptyLibrary({
	canManage,
	onCreated,
}: {
	canManage: boolean
	onCreated: (dashboardId: string) => void
}) {
	const [isCreateOpen, setIsCreateOpen] = useState(false)

	return (
		<div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-center">
			<LayoutDashboardIcon className="text-muted-foreground size-10" />
			<div>
				<p className="font-medium">No dashboards yet</p>
				<p className="text-muted-foreground text-sm">
					{canManage
						? 'Create your first dashboard to get started.'
						: 'No dashboard has been created for your organization yet.'}
				</p>
			</div>

			{canManage && (
				<>
					<Button onClick={() => setIsCreateOpen(true)}>
						<PlusIcon />
						New dashboard
					</Button>

					<CreateDashboardDialog
						open={isCreateOpen}
						onOpenChange={setIsCreateOpen}
						onSaved={onCreated}
					/>
				</>
			)}
		</div>
	)
}
